/**
 * Herbruikbare bouwstenen die exact de klassen uit de stijlreferentie
 * gebruiken (`/root/dashboard-v2-design-reference.html`). Geen eigen CSS:
 * alles komt uit `theme.css`, zodat het beeld één op één klopt.
 */
import { useState } from 'react'
import type { ReactNode } from 'react'
import { SparkChart } from './charts'
import { minutenSinds, urgentieVanMinuten, wachttijdKort } from '../lib/urgentie'
import { initial, num } from '../lib/format'
import { avatarUrl } from '../lib/instance'
import { useInklap } from '../lib/inklappen'

export function Panel({
  title,
  desc,
  right,
  flush,
  inklap,
  children,
}: {
  title: string
  desc?: ReactNode
  right?: ReactNode
  flush?: boolean
  /** 01-10-2026: met een sleutel is dit paneel in te klappen; de stand wordt
   *  per gebruiker op de server bewaard (lib/inklappen). Zonder: zoals altijd. */
  inklap?: string
  children: ReactNode
}) {
  const [dicht, wissel] = useInklap(inklap)
  return (
    <div className="panel">
      <div className="head">
        <div className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
          {inklap ? (
            <button className="btn ghost sm" type="button" onClick={wissel}
                    aria-expanded={!dicht}
                    aria-label={dicht ? `Expand ${title}` : `Collapse ${title}`}
                    title={dicht ? 'Expand' : 'Collapse'}>
              {dicht ? '▸' : '▾'}
            </button>
          ) : null}
          <div>
            <h3>{title}</h3>
            {desc && !dicht ? <p>{desc}</p> : null}
          </div>
        </div>
        {right && !dicht ? <div className="r">{right}</div> : null}
      </div>
      {dicht ? null : <div className={flush ? 'body flush' : 'body'}>{children}</div>}
    </div>
  )
}

/**
 * Een kengetal-kaart.
 *
 * Naast het getal zelf kan er een sparkline in (het verloop over het venster),
 * een doelregel met percentage, en een chip met de verandering tegenover de
 * vorige periode. Het oude dashboard toont die drie ook; ze zijn hier alleen
 * strakker vormgegeven.
 */
export function Kpi({
  label,
  value,
  meta,
  chip,
  icon,
  target,
  spark,
  tone,
  stars,
}: {
  label: string
  value: ReactNode
  meta?: ReactNode
  chip?: { text: string; dir: 'up' | 'down' }
  icon?: string
  /** "€754,99 / €250,00 · 302%" plus of het doel gehaald is. */
  target?: { text: string; gehaald: boolean }
  /** De reeks waarden waar het lijntje van getekend wordt. */
  spark?: number[]
  /** Accentkleur van deze kaart; oud geeft elk kengetal een eigen kleur. */
  tone?: 'ok' | 'acc' | 'acc2' | 'warn' | 'teal' | 'pink'
  /** Nul tot vijf sterren, zoals oud bij de efficiencyscore. */
  stars?: number
}) {
  return (
    <div className={`card kpi spark${tone ? ` t-${tone}` : ''}`}>
      <div className="lbl">
        <span className="g">{icon || '•'}</span>
        {label}
      </div>
      <div className="val">
        {value}
        {stars != null ? (
          <span className="sterren" title={`${stars} of 5`}>
            {'★'.repeat(Math.max(0, Math.min(5, Math.round(stars))))}
            <span className="leeg">
              {'★'.repeat(5 - Math.max(0, Math.min(5, Math.round(stars))))}
            </span>
          </span>
        ) : null}
      </div>
      {spark && spark.length > 1 ? <SparkChart waarden={spark} /> : null}
      {target ? (
        <div className={target.gehaald ? 'kpitarget ok' : 'kpitarget'}>
          <span className="bar">
            <span
              className="fill"
              style={{ width: `${Math.min(100, Math.max(2, Number(
                target.text.split('·').pop()?.replace('%', '').trim() ?? 0)))}%` }}
            />
          </span>
          {target.text}
        </div>
      ) : null}
      {chip ? <span className={`chip ${chip.dir}`}>{chip.text}</span> : null}
      <div className="meta">{meta}</div>
    </div>
  )
}


export function Tag({
  kind,
  children,
  title,
}: {
  kind: 'fc' | 'f2f' | 'ok' | 'warn' | 'bad' | 'acc' | 'mut'
  /** Toelichting bij het aanwijzen, zoals de badges in het oude scherm. */
  title?: string
  children: ReactNode
}) {
  return <span className={`tag ${kind}`} title={title}>{children}</span>
}

/** FC/F2F-label, met dezelfde kleuren als de referentie. */
export function Plat({ value }: { value: unknown }) {
  const v = String(value ?? '').toLowerCase()
  if (v.startsWith('fc') || v === 'fancentro') return <Tag kind="fc">FC</Tag>
  if (v.startsWith('f2f')) return <Tag kind="f2f">F2F</Tag>
  if (v === 'all' || v === 'both') return <Tag kind="acc">ALL</Tag>
  return <Tag kind="mut">{String(value ?? '—')}</Tag>
}

/** Stabiele kleur per model: dezelfde naam krijgt altijd dezelfde tint, zodat
 *  modellen naast elkaar uit elkaar te houden zijn (zoals in Jordy's ontwerp). */
const AV_TINTS = [
  'linear-gradient(135deg,#6d4aff,#a78bfa)',
  'linear-gradient(135deg,#be185d,#ec4899)',
  'linear-gradient(135deg,#1d4ed8,#3b82f6)',
  'linear-gradient(135deg,#b45309,#f59e0b)',
  'linear-gradient(135deg,#0f766e,#2dd4bf)',
  'linear-gradient(135deg,#4338ca,#818cf8)',
]

function tintFor(name: unknown): string {
  const s = String(name ?? '')
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return AV_TINTS[h % AV_TINTS.length]
}

/**
 * Modelfoto, net als in de bundle: `<img src="/avatars/…">`.
 *
 * De bundle toont overal de echte foto — die staat als `avatar_url` op de
 * persona en wordt door nginx (en door de backend-mount) van schijf geserveerd.
 * Deze component toonde eerst alleen een gekleurde letter; dat was het gat.
 * De letter blijft als terugval voor rijen zonder foto en voor een src die
 * niet laadt, zodat de opmaak nooit inzakt.
 */
export function Avatar({
  name,
  src,
  size,
}: {
  name: unknown
  src?: string | null
  size?: number
}) {
  const [broken, setBroken] = useState(false)
  const px = size ?? 22
  // De basis van de fotomap komt van de instantie; NT en TT hebben elk hun
  // eigen map met dezelfde bestandsnamen.
  const bron = avatarUrl(src)
  if (bron && !broken) {
    return (
      <img
        className="av-img"
        src={bron}
        alt={String(name ?? '')}
        loading="lazy"
        width={px}
        height={px}
        onError={() => setBroken(true)}
        style={{ width: px, height: px }}
      />
    )
  }
  return (
    <span
      className="av-s"
      style={{ ['--av' as string]: tintFor(name), width: px, height: px }}
    >
      {initial(name)}
    </span>
  )
}

export function Bar({
  label,
  value,
  max,
  variant,
}: {
  /** Tekst, of bv. een ModelNaam met foto (04-10-2026). */
  label: ReactNode
  value: number
  max: number
  variant?: 'fc' | 'f2f'
}) {
  const w = Math.max(2, Math.round((value / (max || 1)) * 100))
  return (
    <div className="bar">
      <span className="mut">{label}</span>
      <div className="track">
        <div className={`fill ${variant || ''}`} style={{ width: `${w}%` }} />
      </div>
      <span className="num mono">{num(value)}</span>
    </div>
  )
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>
}

export function Loading() {
  return <div className="empty">Loading…</div>
}

export function ErrorNote({ error }: { error: unknown }) {
  const msg = error instanceof Error ? error.message : String(error)
  return (
    <div className="note" style={{ borderColor: 'rgba(255,107,107,.4)' }}>
      <span style={{ color: 'var(--bad)' }}>Could not load this section:</span> {msg}
    </div>
  )
}

/** Tabel met sticky kop; kolommen exact zoals in de inventaris vastgelegd. */
export function Table({
  cols,
  children,
  emptyText,
}: {
  /** 21-10-2026: label mag ook een element zijn (bv. een selecteer-alles-vakje). */
  cols: { key: string; label: ReactNode; num?: boolean }[]
  children: ReactNode
  /** Tekst voor de lege-staat RIJ. De oude bundel houdt de tabelkop staan en
   *  zet er één rij onder als er niets is; dat gedrag nemen we over. */
  emptyText?: string | null
}) {
  const rows = Array.isArray(children) ? children.flat(Infinity) : children
  const isEmpty =
    rows == null ||
    (Array.isArray(rows) && rows.filter(Boolean).length === 0)
  return (
    <div className="tblwrap">
      <table className="tbl">
        <thead>
          <tr>
            {cols.map((c) => (
              <th key={c.key} className={c.num ? 'num' : undefined}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isEmpty && emptyText !== null ? (
            <tr>
              <td colSpan={cols.length} className="empty">
                {emptyText ?? 'No data in this window'}
              </td>
            </tr>
          ) : isEmpty ? null : (
            rows
          )}
        </tbody>
      </table>
    </div>
  )
}

export function Seg({
  options,
  value,
  onChange,
  platform,
}: {
  options: { value: string; label: string }[]
  value: string
  onChange: (v: string) => void
  platform?: boolean
}) {
  return (
    <div className={platform ? 'seg plat' : 'seg'}>
      {options.map((o) => (
        <button
          key={o.value}
          className={o.value === value ? 'on' : undefined}
          onClick={() => onChange(o.value)}
          type="button"
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}


/**
 * De wachttijd als gekleurde chip: groen onder een kwartier, oranje tot een
 * uur, rood daarboven. Rood pulseert zacht.
 *
 * De tijd staat er altijd in cijfers bij. Kleur alleen zou onleesbaar zijn
 * voor wie kleuren slecht onderscheidt, en het getal is toch de informatie
 * waar een chatter op stuurt.
 */
export function Wacht({ sinds, titel }: { sinds: unknown; titel?: string }) {
  const m = minutenSinds(sinds)
  if (m === null) return <span className="dim">—</span>
  const u = urgentieVanMinuten(m)
  return (
    <span className={`wacht ${u}`} title={titel ?? `Waiting ${wachttijdKort(m)}`}>
      <span className="stip" aria-hidden="true" />
      {wachttijdKort(m)}
    </span>
  )
}
